import { useEffect } from 'react'

import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router'

import { generateChatPath } from '@bettermode/common/utils'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  ChatContextProvider,
  DraftChatsProvider,
} from '@tribeplatform/react-components/Messaging'
import {
  useGetChatIdFromSlug,
  useHasChats,
} from '@tribeplatform/react-sdk/hooks'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'

import { MainContent } from './MainContent.js'
import { MessagingLayoutLoading } from './MessagingLayoutLoading.js'
import { SideBar } from './Sidebar.js'
import { isLegacyChat } from './utils/messaging.utils.js'

interface Props {
  /**
   * @default false
   */
  chatOpen?: boolean
  newChat?: boolean
}

export const MessagingLayout = ({ chatOpen = false, newChat }: Props) => {
  const params = useParams<{ slug: string }>()
  const { $t } = useI18n()
  const navigate = useNavigate()
  const { data: hasChats, isLoading: isLoadingHasChats } = useHasChats()
  const { data: chatIdFromSlug, isFetching: isLoadingChatId } =
    useGetChatIdFromSlug({
      variables: { slug: params.slug },
      useQueryOptions: {
        enabled: isLegacyChat(params.slug),
        retry: false,
        refetchOnWindowFocus: false,
      },
    })
  useEffect(() => {
    if (chatIdFromSlug?.chatId) {
      navigate(generateChatPath(chatIdFromSlug.chatId))
    }
  }, [chatIdFromSlug, navigate, params.slug])
  const isLoading = isLoadingHasChats || isLoadingChatId
  const chatId = chatIdFromSlug?.chatId ?? params.slug

  if (isLoading) {
    return <MessagingLayoutLoading />
  }

  return (
    <>
      <Helmet>
        <title>
          {$t({
            id: 'Generics.Messages',
            defaultMessage: 'Messages',
          })}
        </title>
      </Helmet>
      <BackgroundProvider backgroundType="surface">
        <DraftChatsProvider>
          <ChatContextProvider chatId={chatId}>
            <div className="flex bg-surface sm:rounded-base shadow h-full transition-all duration-300 ease-out w-full max-w-full transition-gpu">
              <SideBar chatOpen={chatOpen} chatId={chatId} />
              <MainContent
                hasChats={hasChats}
                newChat={newChat}
                chatOpen={chatOpen}
              />
            </div>
          </ChatContextProvider>
        </DraftChatsProvider>
      </BackgroundProvider>
    </>
  )
}
